import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  exportWaterfallReport,
  exportWaterfallPatients,
  exportWaterfallSummaryRecords,
  getWaterfallPatients,
  getWaterfallCenters,
  getWaterfallReport,
  getWaterfallSummaryRecords,
  type NativeReportQuery,
  type WaterfallPatientQuery,
  type WaterfallPatientResponse,
  type WaterfallPatientScope,
  type WaterfallSummaryMetric,
  type WaterfallSummaryRecordQuery,
  type WaterfallSummaryRecordResponse,
  type WaterfallResponse,
} from '../lib/api/native-operational-reports';
import './WaterfallReportTab.css';

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function rollingTwelveMonthStart() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
}

function monthEnd(month: string) {
  const [year, monthIndex] = month.split('-').map(Number);
  return new Date(Date.UTC(year, monthIndex, 0)).toISOString().slice(0, 10);
}

function monthLabel(month: string, long = false) {
  const [year, monthIndex] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('en-IN', {
    month: 'short',
    year: long ? 'numeric' : '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthIndex - 1, 1))).replace(' ', long ? ' ' : '-');
}

function numericMonthLabel(value: string) {
  const [year, month] = value.slice(0, 7).split('-').map(Number);
  const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1];
  return `${monthName} - ${year}`;
}

function monthDistance(from: string, to: string) {
  const [fromYear, fromMonth] = from.split('-').map(Number);
  const [toYear, toMonth] = to.split('-').map(Number);
  return (toYear - fromYear) * 12 + toMonth - fromMonth;
}

function compactCenter(value: string) {
  return value.replace(/^Ferty9 Fertility Cent(?:er|re)\s*[-–—:]\s*/i, '').trim() || value;
}

function errorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return 'The Waterfall report could not be generated. Check the selected window and try again.';
}

function density(value: number, maximum: number) {
  if (!value || !maximum) return 0;
  const ratio = value / maximum;
  if (ratio >= 0.75) return 4;
  if (ratio >= 0.5) return 3;
  if (ratio >= 0.25) return 2;
  return 1;
}

function total(values: number[]) {
  return values.reduce((sum, value) => sum + value, 0);
}

function elapsedLabel(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
}

export function WaterfallReportTab({
  active,
  centers,
  token,
  canExport,
  canViewSensitive,
  canExportSensitive,
}: {
  active: boolean;
  centers: string[];
  token?: string;
  canExport: boolean;
  canViewSensitive: boolean;
  canExportSensitive: boolean;
}) {
  const defaults = useMemo(() => ({ fromMonth: rollingTwelveMonthStart(), toMonth: currentMonth(), center: 'ALL' }), []);
  const [fromMonth, setFromMonth] = useState(defaults.fromMonth);
  const [toMonth, setToMonth] = useState(defaults.toMonth);
  const [center, setCenter] = useState(defaults.center);
  const [appliedQuery, setAppliedQuery] = useState<NativeReportQuery | null>(null);
  const [data, setData] = useState<WaterfallResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drill, setDrill] = useState<DrillSelection | null>(null);
  const [summaryDrill, setSummaryDrill] = useState<SummaryDrillSelection | null>(null);
  const [reportCenters, setReportCenters] = useState<string[]>(centers);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const loadStartedAt = useRef<number | null>(null);
  const loadController = useRef<AbortController | null>(null);
  useEffect(() => { if (!active) loadController.current?.abort(); return () => loadController.current?.abort(); }, [active]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void getWaterfallCenters(token).then((items) => {
      if (!cancelled) setReportCenters(items.map((item) => item.name));
    }).catch(() => {
      if (!cancelled) setReportCenters(centers);
    });
    return () => { cancelled = true; };
  }, [active, centers, token]);

  const draftQuery = useMemo<NativeReportQuery>(() => ({
    from: `${fromMonth}-01`,
    to: monthEnd(toMonth),
    centers: center === 'ALL' ? [] : [center],
  }), [center, fromMonth, toMonth]);
  const dirty = Boolean(appliedQuery && JSON.stringify(appliedQuery) !== JSON.stringify(draftQuery));
  const selectedMonthCount = monthDistance(fromMonth, toMonth) + 1;
  const isLargeRange = selectedMonthCount > 24;

  useEffect(() => {
    if (!loading || loadStartedAt.current === null) return;
    const updateElapsed = () => setElapsedMs(Date.now() - (loadStartedAt.current || Date.now()));
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1_000);
    return () => window.clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    if (loading || !data || loadStartedAt.current === null) return;
    setElapsedMs(Date.now() - loadStartedAt.current);
    loadStartedAt.current = null;
  }, [data, loading]);

  const load = useCallback(async (query = draftQuery) => {
    if (query.from > query.to) {
      setError('The start month is after the end month. Choose an earlier start month, then generate the report.');
      return;
    }
    if (monthDistance(query.from.slice(0, 7), query.to.slice(0, 7)) > 35) {
      setError('The selected window exceeds 36 months. Narrow the range so the cohort matrix remains readable.');
      return;
    }
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    loadStartedAt.current = Date.now();
    setElapsedMs(0);
    setLoading(true);
    setError('');
    try {
      const response = await getWaterfallReport(token, query, controller.signal);
      if (controller.signal.aborted) return;
      setData(response);
      setAppliedQuery(query);
    } catch (caught) {
      if (controller.signal.aborted) return;
      loadStartedAt.current = null;
      setElapsedMs(null);
      setError(errorMessage(caught));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [draftQuery, token]);

  const reset = () => {
    setFromMonth(defaults.fromMonth);
    setToMonth(defaults.toMonth);
    setCenter(defaults.center);
    setError('');
  };

  const totals = useMemo(() => ({
    newPatients: total(data?.summary.map((row) => row.newPatients) || []),
    advised: total(data?.summary.map((row) => row.totalIvfAdvised) || []),
    matchedOpu: data?.grandTotal || 0,
  }), [data]);
  const conversion = totals.newPatients ? (totals.matchedOpu / totals.newPatients) * 100 : 0;

  return (
    <section
      className="tab-content waterfall-report"
      style={{ display: active ? 'grid' : 'none' }}
      aria-busy={loading}
    >
      <section className="waterfall-controls" aria-labelledby="waterfall-controls-title">
        <div className="waterfall-controls__copy">
          <h2 id="waterfall-controls-title">Cohort window</h2>
          <p>Trace registration cohorts to each patient&apos;s first qualifying package payment.</p>
        </div>
        <div className="waterfall-controls__fields">
          <label>
            <span>Start month</span>
            <input type="month" value={fromMonth} max={toMonth} onChange={(event) => setFromMonth(event.target.value)} />
          </label>
          <label>
            <span>End month</span>
            <input type="month" value={toMonth} min={fromMonth} max={currentMonth()} onChange={(event) => setToMonth(event.target.value)} />
          </label>
          <label>
            <span>Centre</span>
            <select value={center} onChange={(event) => setCenter(event.target.value)}>
              <option value="ALL">All permitted centres</option>
              {reportCenters.map((item) => <option key={item} value={item}>{compactCenter(item)}</option>)}
            </select>
          </label>
          <div className="waterfall-controls__actions">
            <button type="button" className="waterfall-button waterfall-button--quiet" onClick={reset} disabled={loading}>Reset</button>
            <button type="button" className="waterfall-button waterfall-button--primary" onClick={() => void load()} disabled={loading}>
              {loading ? 'Generating…' : dirty ? 'Apply changes' : 'Generate report'}
            </button>
          </div>
        </div>
        <div className="waterfall-controls__status" aria-live="polite">
          <span className={`waterfall-status waterfall-status--${(data?.status || 'NO_DATA').toLowerCase()}`}>
            <i aria-hidden="true" />
            {loading ? 'Loading source evidence' : data?.status === 'READY' ? 'Reconciled' : data?.status === 'PARTIAL_DATA' ? 'Partial source coverage' : 'Awaiting data'}
          </span>
          <span>{data?.coverage.message || 'Uses centre-scoped registration, advice, and clinical-cycle evidence.'}</span>
          <span className={`waterfall-controls__timer${loading ? ' waterfall-controls__timer--running' : ''}`} role="status" aria-live="polite">
            {loading ? 'Report loading time' : 'Full report loaded in'} : <strong>{elapsedMs === null ? 'Not generated' : elapsedLabel(elapsedMs)}</strong>
          </span>
          {isLargeRange ? <strong>{selectedMonthCount}-month range selected — generation and Excel export can take several minutes.</strong> : null}
          {dirty ? <strong>Filters changed — apply them to update the matrix.</strong> : null}
        </div>
      </section>

      {error ? <ReportState tone="error" title="Waterfall generation failed" detail={error} action="Try again" onAction={() => void load()} /> : null}
      {!error && loading && !data ? <WaterfallSkeleton /> : null}

      {!error && data ? (
        <>
          <section className="waterfall-reconcile" aria-label="Waterfall reconciliation summary">
            <div className="waterfall-reconcile__lead">
              <span>Matched conversion</span>
              <strong>{conversion.toLocaleString('en-IN', { maximumFractionDigits: 1 })}%</strong>
              <small>{totals.matchedOpu.toLocaleString('en-IN')} patients converted out of total {totals.newPatients.toLocaleString('en-IN')} registrations from {numericMonthLabel(data.period.from)} to {numericMonthLabel(data.period.to)}</small>
            </div>
            <dl>
              <div><dt>New patients</dt><dd>{totals.newPatients.toLocaleString('en-IN')}</dd></div>
              <div><dt>Total IVF advised</dt><dd>{totals.advised.toLocaleString('en-IN')}</dd></div>
              <div><dt>Package converted</dt><dd>{totals.matchedOpu.toLocaleString('en-IN')}</dd></div>
            </dl>
          </section>

          {data.status === 'NO_DATA' ? (
            <ReportState
              title="No cohort activity in this window"
              detail="No registrations, advice activity, or qualifying package conversions were returned for the selected centres and months."
              action="Reset window"
              onAction={reset}
            />
          ) : (
            <>
              <WaterfallMatrix data={data} canExport={canExport} canDrill={canViewSensitive} loading={loading} query={appliedQuery || draftQuery} token={token} onDrill={setDrill} onSummaryDrill={setSummaryDrill} />
              <WaterfallCohortCards data={data} />
            </>
          )}
        </>
      ) : null}
      {drill && appliedQuery ? <WaterfallPatientDrawer selection={drill} query={appliedQuery} token={token} canExport={canExportSensitive} onClose={() => setDrill(null)} /> : null}
      {summaryDrill && appliedQuery ? <WaterfallSummaryDrawer selection={summaryDrill} query={appliedQuery} token={token} canExport={canExportSensitive} onClose={() => setSummaryDrill(null)} /> : null}
    </section>
  );
}

type DrillSelection = { scope: WaterfallPatientScope; registrationMonth?: string; conversionMonth?: string; title: string };
type SummaryDrillSelection = { metric: WaterfallSummaryMetric; month?: string; title: string };

function WaterfallMatrix({
  data,
  canExport,
  canDrill,
  loading,
  query,
  token,
  onDrill,
  onSummaryDrill,
}: {
  data: WaterfallResponse;
  canExport: boolean;
  canDrill: boolean;
  loading: boolean;
  query: NativeReportQuery;
  token?: string;
  onDrill: (selection: DrillSelection) => void;
  onSummaryDrill: (selection: SummaryDrillSelection) => void;
}) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  type SummaryMetric = 'newPatients' | 'newPatientIvfAdvised' | 'followUpIvfAdvised' | 'totalIvfAdvised' | 'advisedPercentage' | 'advisedNotConverted' | 'totalOpu' | 'revenueConverted' | 'failedPatients' | 'retentionPatients' | 'notRetainedPatients' | 'retainedRatio';
  const summaryRows: Array<{ label: string; key: SummaryMetric; percentage?: boolean }> = [
    { label: 'New patients', key: 'newPatients' },
    { label: 'NP IVF advised', key: 'newPatientIvfAdvised' },
    { label: 'Follow-up IVF advised', key: 'followUpIvfAdvised' },
    { label: 'Total IVF advised', key: 'totalIvfAdvised' },
    { label: 'IVF advised %', key: 'advisedPercentage', percentage: true },
    { label: 'Advised but not converted', key: 'advisedNotConverted' },
    { label: 'Total OPU', key: 'totalOpu' },
    { label: 'Failed', key: 'failedPatients' },
    { label: 'Retained', key: 'retentionPatients' },
    { label: 'Not retained', key: 'notRetainedPatients' },
    { label: 'Retained ratio', key: 'retainedRatio', percentage: true },
    { label: 'Fresh OPU', key: 'revenueConverted' },
  ];
  const summaryMetric = (key: SummaryMetric): WaterfallSummaryMetric | null => ({
    newPatients: 'NEW_PATIENTS', newPatientIvfAdvised: 'NP_IVF_ADVISED', followUpIvfAdvised: 'FOLLOW_UP_IVF_ADVISED', totalIvfAdvised: 'TOTAL_IVF_ADVISED', advisedPercentage: null, advisedNotConverted: 'ADVISED_NOT_CONVERTED', totalOpu: 'TOTAL_OPU', revenueConverted: 'REVENUE_CONVERTED', failedPatients: 'FAILED_PATIENTS', retentionPatients: 'RETENTION_PATIENTS', notRetainedPatients: 'NOT_RETAINED', retainedRatio: null,
  })[key] as WaterfallSummaryMetric | null;
  const summary = data.summary.map((row) => ({ ...row, advisedNotConverted: row.advisedNotConverted ?? 0, totalOpu: row.totalOpu ?? 0, failedPatients: row.failedPatients ?? 0, retentionPatients: row.retentionPatients ?? 0, notRetainedPatients: row.notRetainedPatients ?? 0, retainedRatio: row.retainedRatio ?? 0 }));
  const summaryTotals = summary.reduce((totals, row) => ({
    newPatients: totals.newPatients + row.newPatients,
    newPatientIvfAdvised: totals.newPatientIvfAdvised + row.newPatientIvfAdvised,
    followUpIvfAdvised: totals.followUpIvfAdvised + row.followUpIvfAdvised,
    totalIvfAdvised: totals.totalIvfAdvised + row.totalIvfAdvised,
    advisedPercentage: 0,
    advisedNotConverted: totals.advisedNotConverted + (row.advisedNotConverted ?? 0),
    totalOpu: totals.totalOpu + (row.totalOpu ?? 0),
    failedPatients: totals.failedPatients + (row.failedPatients ?? 0),
    retentionPatients: totals.retentionPatients + (row.retentionPatients ?? 0),
    notRetainedPatients: totals.notRetainedPatients + (row.notRetainedPatients ?? 0),
    retainedRatio: 0,
    revenueConverted: totals.revenueConverted + row.revenueConverted,
  }), { newPatients: 0, newPatientIvfAdvised: 0, followUpIvfAdvised: 0, totalIvfAdvised: 0, advisedPercentage: 0, advisedNotConverted: 0, totalOpu: 0, failedPatients: 0, retentionPatients: 0, notRetainedPatients: 0, retainedRatio: 0, revenueConverted: 0 });
  summaryTotals.advisedPercentage = summaryTotals.newPatients
    ? summaryTotals.totalIvfAdvised / summaryTotals.newPatients * 100
    : 0;
  summaryTotals.retainedRatio = summaryTotals.failedPatients
    ? summaryTotals.retentionPatients / summaryTotals.failedPatients * 100
    : 0;
  const exportReport = async () => {
    setExporting(true);
    setExportError('');
    try {
      await exportWaterfallReport(token, query);
    } catch (caught) {
      setExportError(errorMessage(caught));
    } finally {
      setExporting(false);
    }
  };
  return (
    <section className="waterfall-workbench" aria-labelledby="waterfall-matrix-title">
      <div className="waterfall-workbench__heading">
        <div>
          <h2 id="waterfall-matrix-title">Registration → OPU waterfall</h2>
          <p>Columns are registration cohorts. Rows are first qualifying package-payment months. Each patient is converted once.</p>
        </div>
        <div className="waterfall-workbench__tools">
          <div className="waterfall-legends" aria-label="Cell colour legend">
            <span className="waterfall-legend waterfall-legend--same"><i aria-hidden="true" /> Same-month package</span>
            <span className="waterfall-legend waterfall-legend--later"><i aria-hidden="true" /> Later-month package</span>
          </div>
          <button
            type="button"
            className={`waterfall-button waterfall-button--export${exporting ? ' waterfall-button--working' : ''}`}
            disabled={!canExport || loading || exporting}
            title={canExport ? 'Download the current matrix as an Excel workbook' : 'Business Reports export permission is required'}
            onClick={() => void exportReport()}
          >
            {exporting ? 'Preparing Excel…' : 'Export Excel'}
          </button>
          {exporting ? <span className="waterfall-export-status" role="status" aria-live="polite">Creating the workbook. The download will start automatically.</span> : null}
          {exportError ? <span className="waterfall-export-status waterfall-export-status--error" role="alert">{exportError}</span> : null}
        </div>
      </div>
      <div className="waterfall-matrix-scroll" tabIndex={0} aria-label="Scrollable Waterfall cohort matrix">
        <table className="waterfall-matrix">
          <caption>Monthly registration cohorts followed through each patient&apos;s first qualifying package payment.</caption>
          <thead>
            <tr>
              <th scope="col">Metric / OPU month</th>
              {data.months.map((month) => <th scope="col" key={month}>{monthLabel(month)}</th>)}
              <th scope="col">Overall total</th>
            </tr>
          </thead>
          <tbody>
            {summaryRows.map((definition) => (
              <tr className={`waterfall-matrix__summary waterfall-matrix__summary--${String(definition.key)}`} key={String(definition.key)}>
                <th scope="row">{definition.label}</th>
                {data.summary.map((row) => (
                  <td key={row.month}>{definition.percentage ? <>{Number(row[definition.key]).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%</> : <DrillValue disabled={!canDrill || Number(row[definition.key]) === 0} value={Number(row[definition.key])} onClick={() => onSummaryDrill({ metric: summaryMetric(definition.key)!, month: row.month, title: `${definition.label} — ${monthLabel(row.month, true)}` })} />}</td>
                ))}
                <td>{definition.percentage ? <>{summaryTotals[definition.key].toLocaleString('en-IN', { maximumFractionDigits: 1 })}%</> : <DrillValue disabled={!canDrill || summaryTotals[definition.key] === 0} value={summaryTotals[definition.key]} onClick={() => onSummaryDrill({ metric: summaryMetric(definition.key)!, title: `${definition.label} — overall total` })} />}</td>
              </tr>
            ))}
            <tr className="waterfall-matrix__axis" aria-hidden="true">
              <th>Conversion month ↓</th>
              <td colSpan={data.months.length}>Registration cohort →</td>
              <th>OPU total</th>
            </tr>
            {data.rows.map((row) => (
              <tr className="waterfall-matrix__cohort" key={row.month}>
                <th scope="row">{monthLabel(row.month)}</th>
                {data.months.map((month) => {
                  const value = row.values[month] || 0;
                  const future = month > row.month;
                  const sameMonth = month === row.month;
                  return (
                    <td
                      key={month}
                      className={sameMonth ? 'is-diagonal' : future ? 'is-future' : 'is-delayed'}
                      data-density={future ? undefined : density(value, data.maxCell)}
                      title={future ? 'Registration cohort occurs after this OPU month' : `${value} OPU cycle${value === 1 ? '' : 's'} from the ${monthLabel(month, true)} registration cohort`}
                    >
                      {future ? '' : <DrillValue disabled={!canDrill || value === 0} value={value} onClick={() => onDrill({ scope: 'CELL', registrationMonth: month, conversionMonth: row.month, title: `${monthLabel(month, true)} registrations converted in ${monthLabel(row.month, true)}` })} />}
                    </td>
                  );
                })}
                <td className="waterfall-matrix__row-total"><DrillValue disabled={!canDrill || row.total === 0} value={row.total} onClick={() => onDrill({ scope: 'ROW_TOTAL', conversionMonth: row.month, title: `All patients converted in ${monthLabel(row.month, true)}` })} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Total Converted (New Patient)</th>
              {data.months.map((month) => <td key={month}><DrillValue disabled={!canDrill || !data.columnTotals[month]} value={data.columnTotals[month] || 0} onClick={() => onDrill({ scope: 'COLUMN_TOTAL', registrationMonth: month, title: `All converted patients registered in ${monthLabel(month, true)}` })} /></td>)}
              <td><DrillValue disabled={!canDrill || data.grandTotal === 0} value={data.grandTotal} onClick={() => onDrill({ scope: 'GRAND_TOTAL', title: 'All converted patients in this Waterfall' })} /></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="waterfall-method">
        Advice metrics count each patient at their first-ever IVF advice. Failure and retention metrics follow qualifying package history and recorded outcomes. Conversion is the patient&apos;s first positive qualifying IVF package payment on or after registration and on or before the report end date. Later package payments do not create another conversion. Only patients registered in the selected cohort window are included in the conversion matrix.
      </p>
    </section>
  );
}

function DrillValue({ value, disabled, onClick }: { value: number; disabled: boolean; onClick: () => void }) {
  if (disabled) return <>{value.toLocaleString('en-IN')}</>;
  return <button type="button" className="waterfall-drill-value" onClick={onClick} title="View patient records">{value.toLocaleString('en-IN')}</button>;
}

function useWaterfallDialogFocus(onClose: () => void) {
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = () => Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]') || [])
      .filter((element) => element.offsetParent !== null);
    focusable()[0]?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab') return;
      const items = focusable(), first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    window.addEventListener('keydown', keydown);
    return () => { window.removeEventListener('keydown', keydown); previous?.focus(); };
  }, [onClose]);
  return dialogRef;
}

function WaterfallPatientDrawer({
  selection, query, token, canExport, onClose,
}: {
  selection: DrillSelection; query: NativeReportQuery; token?: string; canExport: boolean; onClose: () => void;
}) {
  const [data, setData] = useState<WaterfallPatientResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const take = 50;
  const request = useMemo<WaterfallPatientQuery>(() => ({
    ...query, scope: selection.scope, registrationMonth: selection.registrationMonth,
    conversionMonth: selection.conversionMonth, search: appliedSearch, skip, take,
  }), [appliedSearch, query, selection, skip]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    void getWaterfallPatients(token, request).then((response) => { if (!cancelled) setData(response); })
      .catch((caught) => { if (!cancelled) setError(errorMessage(caught)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, token]);
  const dialogRef = useWaterfallDialogFocus(onClose);
  const totalRows = data?.pagination.total || 0;
  return (
    <div className="waterfall-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside ref={dialogRef} className="waterfall-drawer" role="dialog" aria-modal="true" aria-labelledby="waterfall-drawer-title">
        <header>
          <div><span>Patient records</span><h2 id="waterfall-drawer-title">{selection.title}</h2><p>{loading ? 'Loading records…' : `${totalRows.toLocaleString('en-IN')} converted patient${totalRows === 1 ? '' : 's'}`}</p></div>
          <button type="button" className="waterfall-drawer__close" onClick={onClose} aria-label="Close patient records">×</button>
        </header>
        <div className="waterfall-drawer__toolbar">
          <form onSubmit={(event) => { event.preventDefault(); setSkip(0); setAppliedSearch(search.trim()); }}>
            <input aria-label="Search patient records" placeholder="Search patient, ID, centre or package" value={search} onChange={(event) => setSearch(event.target.value)} />
            <button type="submit" className="waterfall-button waterfall-button--quiet">Search</button>
          </form>
          <button type="button" className="waterfall-button waterfall-button--export" disabled={!canExport || loading} title={canExport ? 'Export these selected patient records' : 'Sensitive export permission is required'} onClick={() => void exportWaterfallPatients(token, { ...request, skip: 0, take: 50_000 })}>Export selected</button>
        </div>
        {error ? <div className="waterfall-drawer__message" role="alert">{error}</div> : null}
        {!error ? <div className="waterfall-drawer__table"><table><thead><tr><th>Patient</th><th>Patient ID</th><th>Clinic No.</th><th>Centre</th><th>Registered</th><th>Converted</th><th>Delay</th><th>Package</th><th>Invoice</th><th>Amount</th></tr></thead><tbody>
          {data?.rows.map((row, index) => <tr key={`${row.patientId}-${row.conversionDate}-${index}`}><td>{row.patientName || '—'}</td><td>{row.patientId || '—'}</td><td>{row.clinicNumber || row.mrn || '—'}</td><td>{compactCenter(row.centerName)}</td><td>{row.registrationDate}</td><td>{row.conversionDate}</td><td>{row.conversionDelayMonths === 0 ? 'Same month' : `+${row.conversionDelayMonths} month${row.conversionDelayMonths === 1 ? '' : 's'}`}</td><td>{row.packageName}</td><td>{row.invoiceReference || '—'}</td><td>{row.paymentAmount === null ? '—' : row.paymentAmount.toLocaleString('en-IN')}</td></tr>)}
          {!loading && data?.rows.length === 0 ? <tr><td colSpan={10}>No patient records match this selection.</td></tr> : null}
        </tbody></table></div> : null}
        <footer><span>Showing {totalRows ? skip + 1 : 0}–{Math.min(skip + take, totalRows)} of {totalRows.toLocaleString('en-IN')}</span><div><button type="button" className="waterfall-button waterfall-button--quiet" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - take))}>Previous</button><button type="button" className="waterfall-button waterfall-button--quiet" disabled={skip + take >= totalRows || loading} onClick={() => setSkip(skip + take)}>Next</button></div></footer>
      </aside>
    </div>
  );
}

function WaterfallSummaryDrawer({ selection, query, token, canExport, onClose }: { selection: SummaryDrillSelection; query: NativeReportQuery; token?: string; canExport: boolean; onClose: () => void }) {
  const [data, setData] = useState<WaterfallSummaryRecordResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const take = 50;
  const request = useMemo<WaterfallSummaryRecordQuery>(() => ({ ...query, metric: selection.metric, month: selection.month, search: appliedSearch, skip, take }), [appliedSearch, query, selection, skip]);
  useEffect(() => {
    let cancelled = false; setLoading(true); setError('');
    void getWaterfallSummaryRecords(token, request).then((response) => { if (!cancelled) setData(response); }).catch((caught) => { if (!cancelled) setError(errorMessage(caught)); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, token]);
  const dialogRef = useWaterfallDialogFocus(onClose);
  const totalRows = data?.pagination.total || 0;
  const retentionSelection = selection.metric === 'RETENTION_PATIENTS';
  const failedSelection = selection.metric === 'FAILED_PATIENTS';
  const notRetainedSelection = selection.metric === 'NOT_RETAINED';
  const failureSelection = failedSelection || notRetainedSelection;
  const firstIvfAdviceSelection = ['NP_IVF_ADVISED', 'FOLLOW_UP_IVF_ADVISED', 'TOTAL_IVF_ADVISED', 'ADVISED_NOT_CONVERTED'].includes(selection.metric);
  const eventDateLabel = retentionSelection || failureSelection ? 'Failure date' : selection.metric === 'REVENUE_CONVERTED' || selection.metric === 'TOTAL_OPU' ? 'OPU date' : firstIvfAdviceSelection ? 'First IVF advised date' : 'Last advised date';
  const conversionDateLabel = retentionSelection ? 'Second package date' : failureSelection ? 'OPU date' : 'Converted';
  const statusLabel = failureSelection ? 'Outcome' : 'Conversion';
  return <div className="waterfall-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside ref={dialogRef} className="waterfall-drawer" role="dialog" aria-modal="true" aria-labelledby="waterfall-summary-drawer-title">
      <header><div><span>Source records</span><h2 id="waterfall-summary-drawer-title">{selection.title}</h2><p>{loading ? 'Loading records…' : failureSelection ? `${totalRows.toLocaleString('en-IN')} ${notRetainedSelection ? 'not retained' : 'failed'} patients` : `${totalRows.toLocaleString('en-IN')} patients · ${(data?.conversionSummary.distinctConvertedPatients || 0).toLocaleString('en-IN')} converted`}</p></div><button type="button" className="waterfall-drawer__close" onClick={onClose} aria-label="Close source records">×</button></header>
      <div className="waterfall-drawer__toolbar"><form onSubmit={(event) => { event.preventDefault(); setSkip(0); setAppliedSearch(search.trim()); }}><input aria-label="Search source records" placeholder="Search patient, ID, centre or treatment" value={search} onChange={(event) => setSearch(event.target.value)} /><button type="submit" className="waterfall-button waterfall-button--quiet">Search</button></form><button type="button" className="waterfall-button waterfall-button--export" disabled={!canExport || loading} onClick={() => void exportWaterfallSummaryRecords(token, { ...request, skip: 0, take: 50_000 })}>Export selected</button></div>
      {error ? <div className="waterfall-drawer__message" role="alert">{error}</div> : <div className="waterfall-drawer__table"><table><thead><tr><th>Patient ID</th><th>Clinic No.</th><th>Patient Name</th><th>Centre</th><th>Registered</th><th>{eventDateLabel}</th><th>Metric</th><th>Treatment advice</th><th>{statusLabel}</th><th>{conversionDateLabel}</th><th>Delay</th><th>Package</th><th>Invoice</th><th>Amount</th></tr></thead><tbody>{data?.rows.map((row, index) => <tr key={`${row.patientId}-${row.eventDate}-${index}`}><td>{row.patientId || '—'}</td><td>{row.clinicNumber || row.mrn || '—'}</td><td>{row.patientName || '—'}</td><td>{row.centerName ? compactCenter(row.centerName) : '—'}</td><td>{row.registrationDate || '—'}</td><td>{row.eventDate || '—'}</td><td>{(row.metric === 'FAILED_PATIENTS' || row.metric === 'NOT_RETAINED') && row.failureReason ? `${row.metric === 'NOT_RETAINED' ? 'NOT RETAINED' : 'FAILED'} — ${row.failureReason === 'BHCG_NEGATIVE' ? 'BHCG NEGATIVE' : 'ZERO EGGS RETRIEVED'}` : row.metric.replaceAll('_', ' ')}</td><td>{row.treatment || 'Not advised'}</td><td><span className={`waterfall-conversion-status waterfall-conversion-status--${row.conversionStatus.toLowerCase()}`}>{row.conversionStatus === 'FAILED' ? 'Failed' : row.conversionStatus === 'NOT_RETAINED' ? 'Not retained' : row.conversionStatus === 'CONVERTED' ? 'Converted' : 'Not converted'}</span></td><td>{row.conversionDate || '—'}</td><td>{row.conversionDelayMonths === null ? '—' : row.conversionDelayMonths === 0 ? 'Same month' : `+${row.conversionDelayMonths} month${row.conversionDelayMonths === 1 ? '' : 's'}`}</td><td>{row.packageName || '—'}</td><td>{row.invoiceReference || '—'}</td><td>{row.paymentAmount === null ? '—' : row.paymentAmount.toLocaleString('en-IN')}</td></tr>)}{!loading && data?.rows.length === 0 ? <tr><td colSpan={14}>No records match this selection.</td></tr> : null}</tbody></table></div>}
      <footer><span>Showing {totalRows ? skip + 1 : 0}–{Math.min(skip + take, totalRows)} of {totalRows.toLocaleString('en-IN')}</span><div><button type="button" className="waterfall-button waterfall-button--quiet" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - take))}>Previous</button><button type="button" className="waterfall-button waterfall-button--quiet" disabled={skip + take >= totalRows || loading} onClick={() => setSkip(skip + take)}>Next</button></div></footer>
    </aside>
  </div>;
}

function WaterfallCohortCards({ data }: { data: WaterfallResponse }) {
  return (
    <section className="waterfall-cohort-cards" aria-label="Waterfall cohort summaries">
      <div className="waterfall-cohort-cards__heading">
        <h2>Registration cohort summaries</h2>
        <p>Compact registration-to-OPU timing for smaller screens.</p>
      </div>
      {data.summary.map((summary) => {
        const converted = data.columnTotals[summary.month] || 0;
        const sameMonth = data.rows.find((row) => row.month === summary.month)?.values[summary.month] || 0;
        const oneMonthRow = data.rows.find((row) => monthDistance(summary.month, row.month) === 1);
        const oneMonth = oneMonthRow?.values[summary.month] || 0;
        const later = Math.max(0, converted - sameMonth - oneMonth);
        const rate = summary.newPatients ? converted / summary.newPatients * 100 : 0;
        return (
          <article className="waterfall-cohort-card" key={summary.month}>
            <div className="waterfall-cohort-card__header"><h3>{monthLabel(summary.month, true)}</h3><strong>{rate.toLocaleString('en-IN', { maximumFractionDigits: 1 })}%</strong></div>
            <dl>
              <div><dt>New patients</dt><dd>{summary.newPatients.toLocaleString('en-IN')}</dd></div>
              <div><dt>Converted OPU</dt><dd>{converted.toLocaleString('en-IN')}</dd></div>
            </dl>
            <div className="waterfall-cohort-card__timing" aria-label="Conversion timing">
              <span><b>{sameMonth}</b> Same month</span>
              <span><b>{oneMonth}</b> +1 month</span>
              <span><b>{later}</b> +2 months</span>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function WaterfallSkeleton() {
  return (
    <div className="waterfall-skeleton" aria-label="Loading Waterfall report">
      <span /><span /><span /><span />
      <div>{Array.from({ length: 8 }, (_, index) => <i key={index} />)}</div>
    </div>
  );
}

function ReportState({
  tone = 'empty',
  title,
  detail,
  action,
  onAction,
}: {
  tone?: 'empty' | 'error';
  title: string;
  detail: string;
  action: string;
  onAction: () => void;
}) {
  return (
    <section className={`waterfall-state waterfall-state--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19V9m5 10V5m5 14v-7m5 7V3" />
        <path d="M3 19h18" />
      </svg>
      <div><h2>{title}</h2><p>{detail}</p></div>
      <button type="button" className="waterfall-button waterfall-button--quiet" onClick={onAction}>{action}</button>
    </section>
  );
}
